import { type RecordId } from 'components/framework/models/generated/types';
import {
  type ModelName,
  type RecordIdentifier,
} from 'javascripts/shared-types';
import type recentViews from './recentViews.json';
import { getColorObject } from '../../framework/models/concerns/ColorAttribute';

/**
 * Interface for records that can be picked in the RecordPickerModal
 * This is a minimal subset of SearchDocument, including only the properties
 * needed for the modal
 */

interface ColorObject {
  hex(): string;
}
export interface PickableRecord {
  name: string | null;
  searchableType: ModelName;
  searchableId: RecordId | null;
  project?: { name: string } | null;
  searchable: {
    typename: string;
    id: string;
    referenceNum?: string | null;
    path?: string;
    colorObject?: ColorObject | null;
    workflowStatus?: {
      name: string;
      colorObject: ColorObject;
    } | null;
  } | null;

  searchableIdentifier: RecordIdentifier;
}

type RecentViewsResponse = typeof recentViews;

/** Interface for the recent view data returned from the API */
export type RecentView = RecentViewsResponse[number];

const normalizeRecordType = (recordType: string) => {
  if (recordType === 'StrategicImperative') {
    return 'Goal';
  }

  return recordType;
};

/** Convert a RecentView object to a PickableRecord for display in the RecordPickerModal */
export function recentViewToPickableRecord(
  recentView: RecentView
): PickableRecord {
  const recordId = recentView.view_trackable_id as RecordId;
  const recordType = normalizeRecordType(
    recentView.view_trackable_type
  ) as ModelName;

  return {
    name: recentView.searchable_name,
    searchableType: recordType,
    searchableId: recordId,
    project: recentView.project_name ? { name: recentView.project_name } : null,
    searchable: {
      typename: recordType,
      id: recordId,
      referenceNum: recentView.searchable_reference_num,
      path: recentView.url,
      colorObject: getColorObject(recentView.searchable_color),
      workflowStatus: recentView.searchable_status
        ? {
            name: recentView.searchable_status.text,
            colorObject: {
              hex: () => recentView.searchable_status.background_color,
            },
          }
        : null,
    },
    searchableIdentifier: `${recordType}-${recordId}`,
  };
}
